import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { supplyService } from '../services/supplyService';

interface Notifications {
  supplyAlerts: number;
  totalAlerts: number;
}

interface NotificationsContextType {
  notifications: Notifications;
  loading: boolean;
  refreshNotifications: () => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined);

interface NotificationsProviderProps {
  children: ReactNode;
}

export const NotificationsProvider: React.FC<NotificationsProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notifications>({
    supplyAlerts: 0,
    totalAlerts: 0
  });
  const [loading, setLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const supplyAlerts = await supplyService.getSupplyAlertsCount();
      
      setNotifications({
        supplyAlerts,
        totalAlerts: supplyAlerts
      });
    } catch (error) {
      console.error('[Notifications] Error fetching notifications:', error);
      setNotifications({
        supplyAlerts: 0,
        totalAlerts: 0
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Solo inicializar una vez
  useEffect(() => {
    if (!isInitialized) {
      // Check if user is authenticated before making API calls
      const authToken = localStorage.getItem('auth_token');
      if (!authToken) {
        setLoading(false);
        setIsInitialized(true);
        return;
      }
      
      // console.log('[NotificationsContext] Inicializando notificaciones...');
      fetchNotifications();
      setIsInitialized(true);
    }
  }, [fetchNotifications, isInitialized]);

  // Configurar el intervalo solo después de la inicialización
  useEffect(() => {
    if (isInitialized) {
      // Actualizar notificaciones cada 15 minutos
      const interval = setInterval(fetchNotifications, 15 * 60 * 1000);
      
      return () => clearInterval(interval);
    }
  }, [fetchNotifications, isInitialized]);

  const refreshNotifications = useCallback(async () => {
    // Solo refrescar si no está cargando actualmente
    if (!loading) {
      await fetchNotifications();
    }
  }, [fetchNotifications, loading]);

  return (
    <NotificationsContext.Provider value={{
      notifications,
      loading,
      refreshNotifications
    }}>
      {children}
    </NotificationsContext.Provider>
  );
};

export const useNotifications = (): NotificationsContextType => {
  const context = useContext(NotificationsContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationsProvider');
  }
  return context;
};
